import React from "react";
import { useShow, useList } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Button, Card, Table, Tag, Tabs, Spin, Dropdown } from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import Column from "antd/es/table/Column";
import {
    UserOutlined,
    ShoppingCartOutlined,
    FileTextOutlined,
    EditOutlined,
    StarFilled,
    PlusOutlined,
    CheckCircleOutlined,
    MoreOutlined,
    MergeCellsOutlined,
    StopOutlined,
    ExportOutlined,
    BookOutlined,
} from "@ant-design/icons";
import { CustomerAccountingTab } from "../../components/CustomerAccountingTab";
import { PageHeader } from "../../components/ui";
import type { MenuProps } from "antd";

interface CustomerContact {
    id: number;
    phoneNumber: string;
    email: string;
    isPrimary: boolean;
}

interface CustomerAddress {
    id: number;
    houseNo: string;
    street: string;
    city: string;
    province: string;
    country: string;
    isPrimary: boolean;
}

interface Customer {
    id: number;
    customerNumber: string;
    tradeName: string;
    vatNumber: string;
    tpin: string;
    name: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    customerContacts: CustomerContact[];
    customerAddresses: CustomerAddress[];
}

export const CustomerShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "customers",
        id,
        meta: {
            fields: [
                "id",
                "customerNumber",
                "tradeName",
                "vatNumber",
                "tpin",
                "name",
                "description",
                "isActive",
                "createdAt",
                "updatedAt",
                {
                    customerContacts: [
                        "id",
                        "phoneNumber",
                        "email",
                        "isPrimary"
                    ]
                },
                {
                    customerAddresses: [
                        "id",
                        "houseNo",
                        "street",
                        "city",
                        "province",
                        "country",
                        "isPrimary"
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data as Customer | undefined;

    const { data: salesData, isLoading: salesLoading } = useList({
        resource: "sales",
        filters: record?.tpin ? [{ field: "customerTPIN", operator: "eq", value: record.tpin }] : [],
        queryOptions: { enabled: !!record?.tpin },
        meta: {
            fields: [
                "id",
                "invoiceNo",
                "customerName",
                "totalAmount",
                "status",
                "createdAt"
            ]
        }
    });

    const { data: quotationsData, isLoading: quotationsLoading } = useList({
        resource: "quotations",
        filters: record?.tpin ? [{ field: "customerTPIN", operator: "eq", value: record.tpin }] : [],
        queryOptions: { enabled: !!record?.tpin },
        meta: {
            fields: [
                "id",
                "quotationNumber",
                "customerName",
                "totalAmount",
                "status",
                "quotationDate"
            ]
        }
    });

    const { data: saleOrdersData, isLoading: saleOrdersLoading } = useList({
        resource: "saleorders",
        filters: record?.tpin ? [{ field: "customerTPIN", operator: "eq", value: record.tpin }] : [],
        queryOptions: { enabled: !!record?.tpin },
        meta: {
            fields: [
                "id",
                "orderNumber",
                "customerName",
                "totalAmount",
                "status",
                "orderDate"
            ]
        }
    });

    // Calculate lifetime sales
    const lifetimeSales = salesData?.data?.reduce((sum: number, sale: any) => sum + (sale.totalAmount || 0), 0) || 0;

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING":
            case "Draft":
                return "orange";
            case "COMPLETED":
            case "Accepted":
            case "Confirmed":
                return "green";
            case "CANCELLED":
            case "Cancelled":
            case "Rejected":
                return "red";
            case "Sent":
                return "blue";
            case "Expired":
                return "grey";
            case "Converted":
                return "purple";
            default:
                return "default";
        }
    };

    const moreMenuItems = [
        {
            key: "merge",
            label: "Merge Customer",
            icon: <MergeCellsOutlined />,
            onClick: () => console.log("Merge customer"),
        },
        {
            key: "deactivate",
            label: record?.isActive ? "Deactivate" : "Activate",
            icon: <StopOutlined />,
            onClick: () => console.log("Toggle active"),
        },
        {
            key: "export",
            label: "Export Statement",
            icon: <ExportOutlined />,
            onClick: () => console.log("Export statement"),
        },
        {
            key: "ledger",
            label: "View Ledger",
            icon: <BookOutlined />,
            onClick: () => console.log("View ledger"),
        },
    ];

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div className="space-y-4">
                    {/* Customer Information Card */}
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Customer Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Trade Name:</span>
                                    <span className="info-value">{record?.tradeName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">VAT Number:</span>
                                    <span className="info-value">{record?.vatNumber || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Legal Name:</span>
                                    <span className="info-value">{record?.name || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">TIN Number:</span>
                                    <span className="info-value">{record?.tpin || "-"}</span>
                                </div>
                                <div className="info-item full-width">
                                    <span className="info-label">Description:</span>
                                    <span className="info-value">{record?.description || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Status:</span>
                                    <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                        {record?.isActive && <CheckCircleOutlined />}
                                        {record?.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Created At:</span>
                                    <span className="info-value">
                                        <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contacts and Addresses Row */}
                    <div className="two-column-grid">
                        {/* Contacts Card */}
                        <Card
                            title="Contacts"
                            className="data-table-card"
                            extra={
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<EditOutlined />}
                                >
                                    Add Contact
                                </Button>
                            }
                        >
                            <Table
                                dataSource={record?.customerContacts}
                                rowKey={(record) => String(record.id)}
                                pagination={false}
                                size="small"
                            >
                                <Column dataIndex="phoneNumber" title="Phone Number" />
                                <Column dataIndex="email" title="Email" />
                                <Column
                                    dataIndex="isPrimary"
                                    title="Primary"
                                    render={(value: boolean) => (
                                        value ? <StarFilled className="primary-star" /> : null
                                    )}
                                />
                                <Column
                                    title=""
                                    width={40}
                                    render={() => (
                                        <EditOutlined className="edit-icon-btn" />
                                    )}
                                />
                            </Table>
                        </Card>

                        {/* Addresses Card */}
                        <Card
                            title="Addresses"
                            className="data-table-card"
                            extra={
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<EditOutlined />}
                                >
                                    Add Address
                                </Button>
                            }
                        >
                            <Table
                                dataSource={record?.customerAddresses}
                                rowKey={(record) => String(record.id)}
                                pagination={false}
                                size="small"
                            >
                                <Column dataIndex="houseNo" title="House No" />
                                <Column dataIndex="street" title="Street" />
                                <Column dataIndex="city" title="City" />
                                <Column dataIndex="province" title="Province" />
                                <Column dataIndex="country" title="Country" />
                                <Column
                                    title=""
                                    width={40}
                                    render={() => (
                                        <EditOutlined className="edit-icon-btn" />
                                    )}
                                />
                            </Table>
                        </Card>
                    </div>

                    {/* Full Width Addresses Table (if needed for more detail) */}
                    <Card
                        title="Addresses"
                        className="data-table-card"
                        extra={
                            <Button
                                type="text"
                                size="small"
                                icon={<PlusOutlined />}
                                className="action-btn-outlined"
                            >
                                Add Address
                            </Button>
                        }
                    >
                        <Table
                            dataSource={record?.customerAddresses}
                            rowKey={(record) => String(record.id)}
                            pagination={false}
                            size="small"
                        >
                            <Column dataIndex="houseNo" title="House No" />
                            <Column dataIndex="street" title="Street" />
                            <Column dataIndex="city" title="City" />
                            <Column dataIndex="province" title="Province" />
                            <Column dataIndex="country" title="Country" />
                            <Column
                                dataIndex="isPrimary"
                                title="Primary"
                                render={(value: boolean) => (
                                    value ? <Tag color="blue">Primary</Tag> : null
                                )}
                            />
                            <Column
                                title=""
                                width={40}
                                render={() => (
                                    <EditOutlined className="edit-icon-btn" />
                                )}
                            />
                        </Table>
                    </Card>
                </div>
            ),
        },
        {
            key: 'transactions',
            label: 'Transactions',
            children: (
                <div className="space-y-4">
                    <Card title="Sales" className="data-table-card" loading={salesLoading}>
                        <Table
                            dataSource={salesData?.data}
                            rowKey={(record) => String(record.id)}
                            pagination={{ pageSize: 10 }}
                        >
                            <Column
                                dataIndex="invoiceNo"
                                title="Invoice No"
                                render={(text: string, record: any) => (
                                    <Link to={`/sales/show/${record.id}`}>{text}</Link>
                                )}
                            />
                            <Column dataIndex="customerName" title="Customer Name" />
                            <Column
                                dataIndex="totalAmount"
                                title="Total Amount"
                                render={(value: number) => `$${value?.toFixed(2)}`}
                            />
                            <Column
                                dataIndex="status"
                                title="Status"
                                render={(status: string) => (
                                    <Tag color={getStatusColor(status)}>{status}</Tag>
                                )}
                            />
                            <Column
                                dataIndex="createdAt"
                                title="Created At"
                                render={(value: string) => (
                                    <DateField value={value} format="YYYY-MM-DD HH:mm:ss" />
                                )}
                            />
                        </Table>
                    </Card>

                    <Card title="Quotations" className="data-table-card" loading={quotationsLoading}>
                        <Table
                            dataSource={quotationsData?.data}
                            rowKey={(record) => String(record.id)}
                            pagination={{ pageSize: 10 }}
                        >
                            <Column
                                dataIndex="quotationNumber"
                                title="Quotation No"
                                render={(text: string, record: any) => (
                                    <Link to={`/quotations/show/${record.id}`}>{text}</Link>
                                )}
                            />
                            <Column dataIndex="customerName" title="Customer Name" />
                            <Column
                                dataIndex="totalAmount"
                                title="Total Amount"
                                render={(value: number) => `$${value?.toFixed(2)}`}
                            />
                            <Column
                                dataIndex="status"
                                title="Status"
                                render={(status: string) => (
                                    <Tag color={getStatusColor(status)}>{status}</Tag>
                                )}
                            />
                            <Column
                                dataIndex="quotationDate"
                                title="Quotation Date"
                                render={(value: string) => (
                                    <DateField value={value} format="YYYY-MM-DD" />
                                )}
                            />
                        </Table>
                    </Card>

                    <Card title="Sale Orders" className="data-table-card" loading={saleOrdersLoading}>
                        <Table
                            dataSource={saleOrdersData?.data}
                            rowKey={(record) => String(record.id)}
                            pagination={{ pageSize: 10 }}
                        >
                            <Column
                                dataIndex="orderNumber"
                                title="Order No"
                                render={(text: string, record: any) => (
                                    <Link to={`/sale-orders/show/${record.id}`}>{text}</Link>
                                )}
                            />
                            <Column dataIndex="customerName" title="Customer Name" />
                            <Column
                                dataIndex="totalAmount"
                                title="Total Amount"
                                render={(value: number) => `$${value?.toFixed(2)}`}
                            />
                            <Column
                                dataIndex="status"
                                title="Status"
                                render={(status: string) => (
                                    <Tag color={getStatusColor(status)}>{status}</Tag>
                                )}
                            />
                            <Column
                                dataIndex="orderDate"
                                title="Order Date"
                                render={(value: string) => (
                                    <DateField value={value} format="YYYY-MM-DD" />
                                )}
                            />
                        </Table>
                    </Card>
                </div>
            ),
        },
        {
            key: 'details',
            label: 'Customer Details',
            children: (
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Full Customer Details</h3>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/customers/edit/${id}`)}
                        >
                            Edit
                        </Button>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Customer Number:</span>
                                <span className="info-value">{record?.customerNumber || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Trade Name:</span>
                                <span className="info-value">{record?.tradeName || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Legal Name:</span>
                                <span className="info-value">{record?.name || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">VAT Number:</span>
                                <span className="info-value">{record?.vatNumber || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">TIN Number:</span>
                                <span className="info-value">{record?.tpin || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                    {record?.isActive && <CheckCircleOutlined />}
                                    {record?.isActive ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="info-item full-width">
                                <span className="info-label">Description:</span>
                                <span className="info-value">{record?.description || "-"}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Last Updated:</span>
                                <span className="info-value">
                                    <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'accounting',
            label: 'Accounting',
            children: record?.id ? <CustomerAccountingTab customerId={record.id} /> : null,
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="show-page-wrapper">
            <PageHeader
                title={record?.tradeName || "Customer"}
                titleIcon={<UserOutlined />}
                breadcrumbs={[
                    { title: "Customers", href: "/customers" },
                    { title: "Show Customer" },
                ]}
                actions={[
                    {
                        label: "New Sale",
                        icon: <ShoppingCartOutlined />,
                        href: `/sales/create?customerId=${id}`,
                        variant: "outlined",
                    },
                    {
                        label: "New Quotation",
                        icon: <FileTextOutlined />,
                        href: `/quotations/create?customerId=${id}`,
                        variant: "outlined",
                    },
                ]}
                moreMenuItems={moreMenuItems}
            >
                {/* Customer-specific mini stat cards */}
                <div className="mini-stats-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Lifetime Sales</div>
                        <div className="mini-stat-value">${lifetimeSales.toFixed(2)}</div>
                    </div>

                </div>
            </PageHeader>

            <Tabs defaultActiveKey="overview" items={tabItems} />
        </div>
    );
};
